import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { Switch } from '@/components/ui/switch';
import { type SearchAlertChannelValues, type SearchAlertFormErrors } from '@/types/search-alert';

interface SearchAlertChannelFieldsProps {
    values: SearchAlertChannelValues;
    onChange: (values: SearchAlertChannelValues) => void;
    errors?: SearchAlertFormErrors;
    /** Term shown next to the consent checkbox (config/search_alerts.php). */
    consentText: string;
    /** The account already has a mobile: the phone field is not shown. */
    hasPhone: boolean;
    /** Consent is required when WhatsApp is being turned on (always on creation). */
    requireConsent?: boolean;
    disabled?: boolean;
    /** Prefix of the field ids, to render more than one form on the same page. */
    idPrefix?: string;
}

/**
 * E-mail / WhatsApp channels of a search alert (CN-192). When WhatsApp is on, the consent checkbox is mandatory and
 * the mobile is asked only when the account has none; the backend enforces the same rules.
 */
export function SearchAlertChannelFields({
    values,
    onChange,
    errors = {},
    consentText,
    hasPhone,
    requireConsent = true,
    disabled = false,
    idPrefix = 'search-alert',
}: SearchAlertChannelFieldsProps) {
    const emailId = `${idPrefix}-channel-email`;
    const whatsappId = `${idPrefix}-channel-whatsapp`;
    const consentId = `${idPrefix}-whatsapp-consent`;
    const phoneId = `${idPrefix}-phone`;
    const showWhatsappFields = values.channel_whatsapp;

    return (
        <fieldset className="space-y-4" disabled={disabled}>
            <legend className="text-sm font-medium text-gray-900">Como avisar</legend>

            <div className="flex items-center justify-between gap-3">
                <Label htmlFor={emailId} className="font-normal text-gray-700">
                    E-mail
                </Label>
                <Switch
                    id={emailId}
                    checked={values.channel_email}
                    onCheckedChange={(checked) => onChange({ ...values, channel_email: checked })}
                    className="data-[state=checked]:bg-[#0027b2]"
                />
            </div>

            <div className="flex items-center justify-between gap-3">
                <Label htmlFor={whatsappId} className="font-normal text-gray-700">
                    WhatsApp
                </Label>
                <Switch
                    id={whatsappId}
                    checked={values.channel_whatsapp}
                    onCheckedChange={(checked) => onChange({ ...values, channel_whatsapp: checked })}
                    className="data-[state=checked]:bg-[#0027b2]"
                />
            </div>

            {errors.channel_email && (
                <p role="alert" className="text-sm text-red-600">
                    {errors.channel_email}
                </p>
            )}

            {showWhatsappFields && (
                <div className="space-y-4 rounded-lg bg-gray-50 p-3">
                    {/* The account phone is not editable here, but its error (e.g. a landline) must still be shown. */}
                    {hasPhone && errors.phone && (
                        <p id={`${phoneId}-error`} role="alert" className="text-sm text-red-600">
                            {errors.phone}
                        </p>
                    )}

                    {!hasPhone && (
                        <div className="space-y-1.5">
                            <Label htmlFor={phoneId}>Celular com DDD</Label>
                            <PhoneInput
                                id={phoneId}
                                name="phone"
                                value={values.phone}
                                onValueChange={(phone) => onChange({ ...values, phone })}
                                autoComplete="tel-national"
                                aria-invalid={Boolean(errors.phone)}
                                aria-describedby={errors.phone ? `${phoneId}-error` : undefined}
                            />
                            {errors.phone && (
                                <p id={`${phoneId}-error`} role="alert" className="text-sm text-red-600">
                                    {errors.phone}
                                </p>
                            )}
                        </div>
                    )}

                    {requireConsent && (
                        <div className="space-y-1.5">
                            <div className="flex items-start gap-2">
                                <Checkbox
                                    id={consentId}
                                    checked={values.whatsapp_consent}
                                    onCheckedChange={(checked) => onChange({ ...values, whatsapp_consent: checked === true })}
                                    aria-invalid={Boolean(errors.whatsapp_consent)}
                                    aria-describedby={errors.whatsapp_consent ? `${consentId}-error` : undefined}
                                    className="mt-0.5"
                                />
                                <Label htmlFor={consentId} className="cursor-pointer text-sm leading-snug font-normal text-gray-700">
                                    Aceito receber alertas por WhatsApp
                                </Label>
                            </div>
                            {consentText && <p className="pl-6 text-xs leading-snug text-gray-500">{consentText}</p>}
                            {errors.whatsapp_consent && (
                                <p id={`${consentId}-error`} role="alert" className="pl-6 text-sm text-red-600">
                                    {errors.whatsapp_consent}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            )}
        </fieldset>
    );
}
